import type { MayBe, Nullable } from 'types/common';

import {
  ATTEMPTS_BEFORE_REPORT,
  FAILURE_POLICIES,
  LOG_PREFIX,
  NEWLAND_VENDOR_ID,
  RETRY_DELAY_MS,
} from 'models/QRCodeScannerDevice/constants';
import { reportScannerFailureToSentry } from 'models/QRCodeScannerDevice/sentry';
import {
  QrCodeListener,
  ScannerConnectionFailureReason,
  ScannerFailureCause,
  ScannerKioskContext,
} from 'models/QRCodeScannerDevice/types';
import { describeDevice, findScanner, runSafely, serializeDevices } from 'models/QRCodeScannerDevice/utils';

/**
 * Newland QR code scanner plugged on Partteam kiosks, read through WebHID (HID POS mode).
 * Robustness contract: nothing here may throw to the caller or stop the connection loop.
 * Every failure is logged, reported to Sentry and retried.
 */
class QRCodeScannerDevice {
  private static instance: QRCodeScannerDevice;

  private device: Nullable<HIDDevice> = null;

  private buffer = new Uint8Array();

  private listeners = new Set<QrCodeListener>();

  private kioskContext: MayBe<ScannerKioskContext>;

  private failedAttempts = 0;

  private hasReportedFailure = false;

  // Same reference to attach and detach: a replugged scanner must never get two listeners
  private readonly onInputReport = (event: HIDInputReportEvent) => this.handleInputReport(event);

  /**
   * The initial kiosk context is only used when the scanner is created, so that even a failure reported on the
   * very first connection attempt carries it. Use `setKioskContext` to update it afterwards.
   */
  public static getInstance(initialKioskContext?: ScannerKioskContext): QRCodeScannerDevice {
    if (!this.instance) {
      this.instance = new QRCodeScannerDevice(initialKioskContext);
    }

    return this.instance;
  }

  private constructor(kioskContext: MayBe<ScannerKioskContext>) {
    this.kioskContext = kioskContext;

    this.listenToDisconnection();
    this.tryConnect();
  }

  public setKioskContext(kioskContext: ScannerKioskContext): void {
    this.kioskContext = kioskContext;
  }

  public addListener(listener: QrCodeListener): void {
    this.listeners.add(listener);
  }

  public removeListener(listener: QrCodeListener): void {
    this.listeners.delete(listener);
  }

  private listenToDisconnection(): void {
    try {
      navigator.hid?.addEventListener('disconnect', ({ device }) => this.handleDisconnection(device));
    } catch (error) {
      runSafely(() => console.log(`${LOG_PREFIX} Cannot listen to HID disconnections`, error));
    }
  }

  private handleDisconnection(device: HIDDevice): void {
    if (!this.device || device !== this.device) {
      return;
    }

    this.device = null;
    // A scan cut by the unplug must not be merged into the first scan after the reconnection
    this.buffer = new Uint8Array();
    this.detachScanner(device);
    this.tryConnect();
  }

  // The scanner may already be gone: failing to detach it must never prevent the reconnection
  private detachScanner(device: HIDDevice): void {
    runSafely(() => {
      console.log(`${LOG_PREFIX} Scanner unplugged: ${describeDevice(device)}, reconnecting...`);
      device.removeEventListener('inputreport', this.onInputReport);
    });
  }

  // Entry point of every connection attempt: nothing thrown below may stop the retry loop
  private async tryConnect(): Promise<void> {
    try {
      await this.connect();
    } catch (error) {
      this.handleFailure({ reason: ScannerConnectionFailureReason.UNEXPECTED_ERROR, error });
    }
  }

  private async connect(): Promise<void> {
    if (!this.ensureNotConnected()) {
      return;
    }

    console.log(`${LOG_PREFIX} Init attempt #${this.failedAttempts + 1} - looking for vendorId ${NEWLAND_VENDOR_ID}`);

    if (!this.ensureWebHidAvailable()) {
      return;
    }

    const devices = await this.getAllowedDevices();
    if (!devices) {
      return;
    }

    const scanner = this.selectScanner(devices);
    if (!scanner) {
      return;
    }

    const isOpened = await this.openScanner(scanner, devices);
    if (!isOpened) {
      return;
    }

    this.onScannerConnected(scanner);
  }

  // Returns false, and logs it, when a scanner is already connected
  private ensureNotConnected(): boolean {
    if (this.device) {
      console.log(`${LOG_PREFIX} Init skipped, scanner already set: ${describeDevice(this.device)}`);
      return false;
    }

    return true;
  }

  // Returns false, and handles the failure, when the browser has no WebHID
  private ensureWebHidAvailable(): boolean {
    if (navigator.hid) {
      return true;
    }

    this.handleFailure({ reason: ScannerConnectionFailureReason.WEBHID_UNAVAILABLE });
    return false;
  }

  private async getAllowedDevices(): Promise<MayBe<HIDDevice[]>> {
    try {
      const devices = (await navigator.hid.getDevices()) ?? [];
      console.log(`${LOG_PREFIX} Allowed HID devices (${devices.length}):`, devices.map(describeDevice), serializeDevices(devices));
      return devices;
    } catch (error) {
      this.handleFailure({ reason: ScannerConnectionFailureReason.GET_DEVICES_FAILED, error });
      return undefined;
    }
  }

  private selectScanner(devices: HIDDevice[]): MayBe<HIDDevice> {
    const lookup = findScanner(devices);
    if ('reason' in lookup) {
      this.handleFailure({ reason: lookup.reason, devices });
      return undefined;
    }

    console.log(`${LOG_PREFIX} Scanner found: ${describeDevice(lookup.device)}, opening...`);
    return lookup.device;
  }

  private async openScanner(scanner: HIDDevice, devices: HIDDevice[]): Promise<boolean> {
    try {
      // Already opened (e.g. by hand from the DevTools console): opening it again would throw forever
      if (!scanner.opened) {
        await scanner.open();
      }
      return true;
    } catch (error) {
      this.handleFailure({ reason: ScannerConnectionFailureReason.OPEN_FAILED, devices, error });
      return false;
    }
  }

  private onScannerConnected(scanner: HIDDevice): void {
    // Attached only once opened: a failed open is retried, a listener added before would be duplicated
    scanner.addEventListener('inputreport', this.onInputReport);
    this.device = scanner;
    // A later failure (scanner unplugged for good) is a new incident: allow a new report
    this.failedAttempts = 0;
    this.hasReportedFailure = false;
    console.log(`${LOG_PREFIX} Scanner opened: ${describeDevice(scanner)}`);
  }

  private handleFailure({ reason, devices = [], error }: ScannerFailureCause): void {
    this.failedAttempts += 1;

    // Logging or reporting must never prevent the retry below
    runSafely(() => this.notifyFailure({ reason, devices, error }));

    if (FAILURE_POLICIES[reason].isPermanent) {
      runSafely(() => console.log(`${LOG_PREFIX} Giving up: ${reason} cannot be fixed by retrying`));
      return;
    }

    setTimeout(() => this.tryConnect(), RETRY_DELAY_MS);
  }

  private notifyFailure({ reason, devices = [], error }: ScannerFailureCause): void {
    console.log(`${LOG_PREFIX} Scanner not connected (attempt #${this.failedAttempts}): ${reason}`, devices.map(describeDevice), error ?? '');

    if (this.isReportDue(reason)) {
      this.hasReportedFailure = true;
      reportScannerFailureToSentry({ reason, devices, attempts: this.failedAttempts, kioskContext: this.kioskContext, error });
    }
  }

  // One report per incident: errors right away, "not there yet" states after the boot grace period
  private isReportDue(reason: ScannerConnectionFailureReason): boolean {
    if (this.hasReportedFailure) {
      return false;
    }

    return FAILURE_POLICIES[reason].isReportedImmediately || this.failedAttempts >= ATTEMPTS_BEFORE_REPORT;
  }

  private handleInputReport({ data }: HIDInputReportEvent): void {
    const qrCode = new Uint8Array(data.buffer);
    // The format of the qrCode is the following:
    // Byte 0 : report ID
    // Byte 1 : Barcode Length
    // Byte 2 - 57 : Decoded data
    // Byte 58 - 61 : Reserved
    // Byte 62 : Newland Symbology Identifier
    // Byte 63 : Check if all data is received

    // Get the decoded data
    // A packet shorter than max capacity (56 bytes) means it's the last one
    const actualLength = qrCode[0];
    const isLastPart = actualLength < 56;
    const decodedData = qrCode.slice(1, actualLength + 1);
    console.log('[QR Code HID] - Input report :', qrCode);
    if (actualLength === 0) {
      return;
    }

    const mergedBuffer = new Uint8Array(this.buffer.length + decodedData.length);
    mergedBuffer.set(this.buffer);
    mergedBuffer.set(decodedData, this.buffer.length);
    this.buffer = mergedBuffer;
    console.log('[QR Code HID] - Buffer value - after merging: ', this.buffer);
    if (isLastPart) {
      const decodedDataString = new TextDecoder().decode(this.buffer);
      console.log('[QR Code HID] - End of buffer reader, sending buffer to callback', this.buffer);

      // Remove blank characters
      const [qrCodeToken] = decodedDataString.split('\r');
      this.listeners.forEach((listener) => listener(qrCodeToken));

      this.buffer = new Uint8Array();
    }
  }
}

export default QRCodeScannerDevice;
